/**
 * Роутер MES React-клієнта.
 *
 * Шляхи:
 *   /                  — Home: якщо залогінений → редирект на рольовий
 *                        landing (landingForRoles, lib/nav.ts), інакше →
 *                        /login.
 *   /login             — публічно, форма QR-токена. Веде на той самий
 *                        рольовий landing (швея/ВТО → /sewing).
 *   /workspace         — прибрано 2026-07-09 (старий Sprint 9a артефакт);
 *                        редирект на "/" для сумісності зі старими лінками.
 *   /orders            — список замовлень (всі role-and).
 *   /orders/:id        — картка замовлення + timeline (correction для admin/master).
 *   /admin/orders/new  — створення замовлення (Sprint 9c, admin/master only).
 *   /health-check      — diagnostics-сторінка (раніше була /).
 *   *                  — 404.
 */
import { Routes, Route, Link, Navigate, useLocation, useNavigationType, useParams } from "react-router-dom";
import { Suspense, lazy, useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import { isStaleChunkError, reloadToNewVersion } from "@/lib/staleBuild";
import { ToastViewport } from "@/components/ToastViewport";
import { EnvBanner } from "@/components/EnvBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ConfirmProvider } from "@/components/ui/ConfirmDialog";
import { RequireAuth } from "@/components/RequireAuth";
import { DevImpersonationBar } from "@/components/DevImpersonationBar";
import { UpdateWatcher } from "@/components/UpdateWatcher";
import { AlertsWatcher } from "@/components/AlertsWatcher";
import { useAuthStore } from "@/store/auth";
import { landingForRoles } from "@/lib/landing";
// Login лишаємо eager — це перший екран для неавторизованого, без спінера.
import LoginPage from "@/pages/Login";

// Решта екранів — code-split: Vite ріже кожен на окремий чанк, тож швея на 3G
// тягне лише свій кабінет, а не всю адмінку (recharts/gantt/kanban тощо).
const ProblemsPage = lazy(() => import("@/pages/problems/Problems"));
const PrintOrders = lazy(() => import("@/pages/orders/PrintOrders"));
const PrintLabels = lazy(() => import("@/pages/orders/PrintLabels"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallback"));
const WaitingPage = lazy(() => import("@/pages/Waiting"));
// Бейдж прочитано, але робочого екрана для цієї спеціальності ще немає.
// До 04.09.2026 така людина падала на /orders і діставала 403 без причини.
const NoScreenPage = lazy(() => import("@/pages/NoScreen"));
const TerminalScreen = lazy(() => import("@/pages/TerminalScreen"));
const OrdersListPage = lazy(() => import("@/pages/OrdersList"));
const OrderDetailPage = lazy(() => import("@/pages/OrderDetail"));
const OrderCreatePage = lazy(() => import("@/pages/admin/OrderCreate"));
const SeamstressesListPage = lazy(() => import("@/pages/admin/SeamstressesList"));
const AttendanceJournalPage = lazy(() => import("@/pages/admin/AttendanceJournal"));
const ContractorsPage = lazy(() => import("@/pages/admin/Contractors"));
const InvoicesJournalPage = lazy(() => import("@/pages/invoices/InvoicesJournal"));
const InvoiceNewPage = lazy(() => import("@/pages/invoices/InvoiceNew"));
const InvoicePrintPage = lazy(() => import("@/pages/invoices/InvoicePrint"));
const AdminHomePage = lazy(() => import("@/pages/admin/AdminHome"));
const QcHomePage = lazy(() => import("@/pages/qc/QcHome"));
const AcceptScreenPage = lazy(() => import("@/pages/qc/AcceptScreen"));
const TechnologDashboardPage = lazy(() => import("@/pages/technolog/Dashboard"));
const TechDocsEditor = lazy(() => import("@/pages/technolog/TechDocsEditor"));
const ShippingPage = lazy(() => import("@/pages/shipping/Shipping"));
const SalaryPage = lazy(() => import("@/pages/salary/Salary"));
const TechnologPlanning = lazy(() => import("@/pages/technolog/Planning"));
const OwnerDashboardPage = lazy(() => import("@/pages/owner/Dashboard"));
const SewingHomePage = lazy(() => import("@/pages/sewing/Home"));
const VtoHomePage = lazy(() => import("@/pages/vto/Home"));
const DeptTeamPage = lazy(() => import("@/pages/vto/Team"));
const TechcardOrderPage = lazy(() => import("@/pages/TechcardOrderPage"));
const OperationsCatalogPage = lazy(() => import("@/pages/constructor/OperationsCatalogPage"));
const WarehousePage = lazy(() => import("@/pages/stock/WarehousePage"));
const VtoOrderPage = lazy(() =>
  import("@/pages/vto/Home").then((m) => ({ default: m.VtoOrderPage })));
const NewPackScreen = lazy(() => import("@/pages/sewing/NewPack"));
const SubmitScreen = lazy(() => import("@/pages/sewing/Submit"));
const TaskTrackerPage = lazy(() => import("@/pages/tasks/TaskTracker"));
const UploadPage = lazy(() => import("@/pages/upload/Upload"));
const ShipmentTagsPage = lazy(() => import("@/pages/printing/ShipmentTags"));
const ConstructorBoard = lazy(() => import("@/pages/constructor/BoardPage"));
const StockPage = lazy(() => import("@/pages/stock/Stock"));
const CashPage = lazy(() => import("@/pages/cash/Cash"));
const ProjectSummaryPage = lazy(() => import("@/pages/orders/ProjectSummary"));
const AccessMatrixPage = lazy(() => import("@/pages/admin/AccessMatrix"));
const PeopleHub = lazy(() => import("@/pages/admin/PeopleHub"));
const NormsPage = lazy(() => import("@/pages/technolog/Norms"));
const AdminPanelPage = lazy(() => import("@/pages/admin/AdminPanel"));
const TrashAndUndoPage = lazy(() => import("@/pages/admin/TrashAndUndo"));
const TechnologAcceptListPage = lazy(() =>
  import("@/pages/technolog/Accept").then((m) => ({ default: m.TechnologAcceptListPage })));
const TechnologAcceptDetailPage = lazy(() =>
  import("@/pages/technolog/Accept").then((m) => ({ default: m.TechnologAcceptDetailPage })));
const CuttingWorkspaceListPage = lazy(() =>
  import("@/pages/cutting/Workspace").then((m) => ({ default: m.CuttingWorkspaceListPage })));
const CuttingWorkspaceDetailPage = lazy(() =>
  import("@/pages/cutting/Workspace").then((m) => ({ default: m.CuttingWorkspaceDetailPage })));
const ProgressListPage = lazy(() =>
  import("@/pages/progress/Progress").then((m) => ({ default: m.ProgressListPage })));
const ProgressDetailPage = lazy(() =>
  import("@/pages/progress/Progress").then((m) => ({ default: m.ProgressDetailPage })));
const PackingWorkspaceListPage = lazy(() =>
  import("@/pages/packing/Workspace").then((m) => ({ default: m.PackingWorkspaceListPage })));
const PackingWorkspaceDetailPage = lazy(() =>
  import("@/pages/packing/Workspace").then((m) => ({ default: m.PackingWorkspaceDetailPage })));

/** Ролі, яким дозволено створювати замовлення (узгоджено з бек ORDER_WRITE_ROLES). */
const ADMIN_ROLES = ["admin", "master", "menedzher"];

/** Ролі, що бачать адмін-розділ "Працівники" (read+write для admin/master,
 *  read для menedzher — гранулярність керується на бекенді). */
const SEAMSTRESS_ADMIN_ROLES = ["admin", "master", "menedzher"];

/** ОТК-зона: read для otk/admin/master/menedzher, write для otk/admin. */
const QC_READ_ROLES = ["otk", "admin", "master", "menedzher"];
const QC_ACCEPT_ROLES = ["otk", "admin"];

/** Технолог Dashboard — наглядові ролі. */
const TECHNOLOG_ROLES = ["master", "admin", "menedzher"];

/** Закрійник Workspace — закрійник + наглядові. */
const CUTTING_ROLES = ["zakriynyk", "master", "admin", "menedzher"];
const PROGRESS_ROLES = ["zakriynyk", "tsyklichka", "master", "admin", "menedzher"];

/** Пакувальник Workspace — пакувальник + наглядові. */
// ВТО у нас теж пакує — той самий екран, облік іменний з її бейджа.
const PACKING_ROLES = ["pakuvalnyk", "vto", "master", "admin", "menedzher"];

/** Кабінет швеї — суто особистий (потрібен токен швеї). Керівні наглядають
 * через Дашборд/Гант/Швей, а не «входом» у чужий кабінет.
 * Тільки `shveya` — дзеркало бекенду (sewing.py::SEWING_ROLES). Раніше тут був
 * ще `vto`, і ВТО-працівниця за старим лінком проходила guard, але КОЖЕН запит
 * до /api/v1/sewing/* повертав 403 → порожній кабінет без пояснення. Її робочі
 * екрани — /vto і /packing (обидва є в меню). */
const SEWING_ROLES = ["shveya"];

/** Кабінет ВТО — ВТО-працівник + наглядові (керування довідником операцій). */
const VTO_ROLES = ["vto", "master", "admin"];
/** Кабінет Циклічки — її працівник + наглядові. */
const TSYKLICHKA_ROLES = ["tsyklichka", "master", "admin"];

/** Таск-трекер — ОТК + Технолог/менеджер/адмін (узгоджено з qc_tasks API). */
const TASK_ROLES = ["otk", "master", "menedzher", "admin"];

/** Завантаження 1С — менеджер + адмін (узгоджено з uploads API). */
const UPLOAD_ROLES = ["menedzher", "admin"];

/** Друк — друкар + наглядові (узгоджено з printing API). */
const PRINTING_ROLES = ["printer", "master", "admin", "menedzher", "vidgruzka"];

/** Накладні — керівні + відвантаження (узгоджено з invoices API). */
const INVOICE_ROLES = ["admin", "master", "menedzher", "vidgruzka"];

function Home() {
  const isAuth = useAuthStore((s) => s.isAuthenticated());
  const claims = useAuthStore((s) => s.claims);
  if (!isAuth) return <Navigate to="/login" replace />;
  return <Navigate to={landingForRoles(claims?.roles)} replace />;
}

function HealthCheck() {
  const [status, setStatus] = useState<"idle" | "ok" | "fail">("idle");
  const [reqId, setReqId] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get("/health", { headers: { Accept: "text/plain" } })
      .then((r) => {
        setStatus(r.status === 200 ? "ok" : "fail");
        setReqId(r.headers["x-request-id"] ?? null);
      })
      .catch(() => setStatus("fail"));
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full bg-mes-surface rounded-2xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-mes-primary mb-2">MES · Diagnostics</h1>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span>Backend /health</span>
            <span
              className={
                status === "ok"
                  ? "text-mes-success font-semibold"
                  : status === "fail"
                  ? "text-mes-danger font-semibold"
                  : "text-mes-muted"
              }
            >
              {status === "ok" ? "OK" : status === "fail" ? "FAIL" : "..."}
            </span>
          </div>
          {reqId && (
            <div className="flex items-center justify-between text-xs text-mes-muted">
              <span>X-Request-ID</span>
              <code className="font-mono">{reqId.slice(0, 12)}…</code>
            </div>
          )}
        </div>
        <Link to="/" className="min-h-[44px] inline-flex items-center text-mes-primary text-sm mt-4 underline">← На головну</Link>
      </div>
    </div>
  );
}

/** Заглушка на час підвантаження чанка сторінки (code-split). */
/**
 * Що бачить людина, коли екран зламався.
 *
 * Три речі, яких бракувало білій сторінці: сказати, що це збій системи (а не
 * «я щось не так натиснула»), дати кнопку повернутись, і показати технічний
 * рядок — щоб було що переслати в підтримку.
 */
function ScreenCrashed({ err, reset }: { err: Error; reset: () => void }) {
  // Не поломка, а оновлення: екран зі старої збірки вже прибрано з сервера.
  const stale = isStaleChunkError(err);
  const [reloading, setReloading] = useState(stale);
  useEffect(() => {
    if (stale) void reloadToNewVersion().then((ok) => setReloading(ok));
  }, [stale]);
  if (reloading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass rounded-glass p-6 max-w-md w-full text-center">
          <div className="text-3xl mb-2" aria-hidden="true">🔄</div>
          <h1 className="text-lg font-bold m-0 mb-2">Вийшла нова версія — оновлюю…</h1>
          <p className="text-sm text-aurora-muted m-0">Дані нікуди не зникли.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="glass rounded-glass p-6 max-w-md w-full text-center">
        <div className="text-3xl mb-2" aria-hidden="true">⚠️</div>
        <h1 className="text-lg font-bold m-0 mb-2">Цей екран зламався</h1>
        <p className="text-sm text-aurora-muted mb-4">
          Це збій системи, а не ваша помилка. Дані нікуди не зникли —
          спробуйте відкрити екран заново.
        </p>
        <div className="flex gap-2 justify-center mb-4">
          <button
            onClick={reset}
            className="h-11 px-4 rounded-[10px] bg-aurora-primary text-white text-sm font-semibold"
          >
            Спробувати ще раз
          </button>
          <button
            onClick={() => window.location.reload()}
            className="h-11 px-4 rounded-[10px] border border-gray-300 text-sm font-semibold"
          >
            Перезавантажити
          </button>
        </div>
        <details className="text-left">
          <summary className="text-xs text-aurora-subtle cursor-pointer">
            Технічні подробиці (для підтримки)
          </summary>
          <pre className="mt-2 text-[11px] whitespace-pre-wrap break-words text-aurora-muted">
            {err.message}
          </pre>
        </details>
      </div>
    </div>
  );
}


function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-aurora-muted text-sm">
      Завантаження…
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">404 — сторінку не знайдено</h2>
        <Link to="/" className="min-h-[44px] inline-flex items-center text-mes-primary text-sm">← На головну</Link>
      </div>
    </div>
  );
}

/** Старі посилання на проєкт → єдине вікно замовлення. */
function ProjectToOrderRedirect() {
  const { id } = useParams();
  return <Navigate to={`/orders/${id}`} replace />;
}

/** Скан бейджа камерою телефона: /sewing/login/qr/<token> → /login?qr=<token>
 *  (Login сам виконає checkin+вхід). */
function QrBadgeRedirect() {
  const { token } = useParams();
  return <Navigate to={`/login?qr=${encodeURIComponent(token ?? "")}`} replace />;
}

/** Сумісність: старий /cutting/workspace/:id → чистий /cutting/:id. */
function CuttingWorkspaceLegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/cutting/${id}`} replace />;
}

/** Сумісність: старий /packing/workspace/:id → чистий /packing/:id. */
function PackingWorkspaceLegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/packing/${id}`} replace />;
}

/** Новий екран — згори. Роутер сам прокрутку не скидає: закрійник гортав
 *  довгу чергу, тиснув замовлення — і картка відкривалась уже внизу, повз
 *  робочу зону (28.09.2026). Скролиться або вікно, або <main> оболонки. */
function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType();
  // Де стояла прокрутка кожного запису історії — щоб «Назад» повертав туди ж
  // (код-ревю 29.09.2026: без цього список успадковував прокрутку картки).
  useEffect(() => {
    const key = location.key;
    const save = () => scrollPos.set(key, window.scrollY);
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, [location.key]);
  useEffect(() => {
    if (navType === "POP") {
      const y = scrollPos.get(location.key) ?? 0;
      // Дані списку вантажаться після монтування — пробуємо кілька разів,
      // поки сторінка не стане достатньо довгою.
      const ids = [0, 150, 400, 900].map((ms) => window.setTimeout(() => window.scrollTo(0, y), ms));
      return () => ids.forEach((t) => window.clearTimeout(t));
    }
    window.scrollTo(0, 0);
    document.querySelectorAll("main").forEach((m) => {
      m.scrollTop = 0;
    });
  }, [location.key, navType]);
  return null;
}
const scrollPos = new Map<string, number>();

export default function App() {
  return (
    <ConfirmProvider>
      <EnvBanner />
      <ToastViewport />
      <DevImpersonationBar />
      <UpdateWatcher />
      <AlertsWatcher />
      <ScrollToTop />
      {/* Межа помилок навколо ВСІХ маршрутів. Помилка одного екрана більше
          не залишає біле поле: людина бачить, що сталось, і може
          перезавантажити. У консоль помилка йде як і раніше. */}
      <ErrorBoundary fallback={(err, reset) => <ScreenCrashed err={err} reset={reset} />}>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Бейдж кодує ${FRONTEND_URL}/sewing/login/qr/<token> — камера
            телефона відкриває цей шлях; передаємо токен у Login через ?qr= */}
        <Route path="/sewing/login/qr/:token" element={<QrBadgeRedirect />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* Кімната очікування для незнайомого Google-акаунта (публічна, без токенів) */}
        <Route path="/waiting" element={<WaitingPage />} />
        <Route path="/no-screen" element={<NoScreenPage />} />
        <Route path="/terminal" element={<TerminalScreen />} />
        <Route path="/health-check" element={<HealthCheck />} />
        {/* /workspace — старий Sprint 9a артефакт (WorkspacePage), прибрано з
            маршрутизації 2026-07-09. Хто завітає за старим лінком/бейджем —
            редиректимо на "/", звідки Home сам обчислить рольовий landing
            (швея/ВТО → /sewing, її реальне робоче місце). */}
        <Route path="/workspace" element={<Navigate to="/" replace />} />
        <Route
          path="/sewing"
          element={
            <RequireAuth requireRoles={SEWING_ROLES} sect="sewing">
              <SewingHomePage />
            </RequireAuth>
          }
        />
        <Route
          path="/vto"
          element={
            <RequireAuth requireRoles={VTO_ROLES} sect="vto">
              <VtoHomePage />
            </RequireAuth>
          }
        />
        <Route
          path="/vto/order/:orderId"
          element={
            <RequireAuth requireRoles={VTO_ROLES} sect="vto">
              <VtoOrderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/tsyklichka"
          element={
            <RequireAuth requireRoles={TSYKLICHKA_ROLES} sect="tsyklichka">
              <VtoHomePage dept="tsyklichka" />
            </RequireAuth>
          }
        />
        <Route
          path="/tsyklichka/order/:orderId"
          element={
            <RequireAuth requireRoles={TSYKLICHKA_ROLES} sect="tsyklichka">
              <VtoOrderPage dept="tsyklichka" />
            </RequireAuth>
          }
        />
        <Route
          path="/tsyklichka/team"
          element={
            <RequireAuth requireRoles={TSYKLICHKA_ROLES} sect="tsyklichka">
              <DeptTeamPage />
            </RequireAuth>
          }
        />
        <Route
          path="/sewing/pack/new/:orderId"
          element={
            <RequireAuth requireRoles={SEWING_ROLES} sect="sewing">
              <NewPackScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/sewing/submit/:token"
          element={
            <RequireAuth requireRoles={SEWING_ROLES} sect="sewing">
              <SubmitScreen />
            </RequireAuth>
          }
        />
        <Route
          path="/tasks"
          element={
            <RequireAuth requireRoles={TASK_ROLES} sect="tasks">
              <TaskTrackerPage />
            </RequireAuth>
          }
        />
        <Route
          path="/upload"
          element={
            <RequireAuth requireRoles={UPLOAD_ROLES} sect="uploads">
              <UploadPage />
            </RequireAuth>
          }
        />
        <Route
          path="/printing"
          element={
            <RequireAuth requireRoles={PRINTING_ROLES} sect="printing">
              <ShipmentTagsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/dev-project"
          // «Бізнес-процеси» прибрано 24.09.2026 (рішення власника); бюджет
          // розробки живе в «Аналітика та план». Старі закладки ведемо туди.
          element={<Navigate to="/technolog/dashboard" replace />}
        />
        <Route
          path="/trash"
          element={
            <RequireAuth requireRoles={["admin", "master", "menedzher", "buhgalter"]}>
              <TrashAndUndoPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/panel"
          element={
            <RequireAuth requireRoles={["admin"]} sect="admin.panel">
              {/* Лише службове. Кошик майстра — у «Кошик і відкат» (/trash). */}
              <AdminPanelPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders"
          element={
            <RequireAuth>
              <OrdersListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <RequireAuth>
              <OrderDetailPage />
            </RequireAuth>
          }
        />
        {/* Тех-карта замовлення — читає кожен, хто бачить замовлення (бек
            пускає за ORDER_READ_ROLES). */}
        <Route
          path="/orders/:id/techcard"
          element={
            <RequireAuth>
              <TechcardOrderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth requireRoles={["admin", "master", "menedzher", "buhgalter"]} sect="admin-home">
              <AdminHomePage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/orders/new"
          element={
            <RequireAuth requireRoles={[...ADMIN_ROLES, "konstruktor"]}>
              <OrderCreatePage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/seamstresses"
          element={
            <RequireAuth requireRoles={SEAMSTRESS_ADMIN_ROLES} sect="seamstresses">
              <SeamstressesListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/attendance"
          element={
            <RequireAuth requireRoles={SEAMSTRESS_ADMIN_ROLES} sect="attendance">
              <AttendanceJournalPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/contractors"
          element={
            <RequireAuth requireRoles={SEAMSTRESS_ADMIN_ROLES} sect="contractors">
              <ContractorsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/invoices"
          element={
            <RequireAuth requireRoles={INVOICE_ROLES} sect="invoices">
              <InvoicesJournalPage />
            </RequireAuth>
          }
        />
        <Route
          path="/invoices/new"
          element={
            <RequireAuth requireRoles={INVOICE_ROLES} sect="invoices">
              <InvoiceNewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/invoices/:id/edit"
          element={
            <RequireAuth requireRoles={INVOICE_ROLES} sect="invoices">
              <InvoiceNewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/invoices/:id/print"
          element={
            <RequireAuth requireRoles={INVOICE_ROLES} sect="invoices">
              <InvoicePrintPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/salary"
          element={
            <RequireAuth requireRoles={["admin", "buhgalter", "master"]} sect="salary">
              <SalaryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/qc"
          element={
            <RequireAuth requireRoles={QC_READ_ROLES} sect="qc">
              <QcHomePage />
            </RequireAuth>
          }
        />
        {/* Тех-доки живуть вкладкою «📐 Тех-доки» всередині кабінету ОТК —
            там, де ними користуються. Окрему сторінку прибрано: плодити
            вкладки заради того самого списку немає сенсу. Редірект — для
            старих закладок. */}
        <Route path="/qc/tech-docs" element={<Navigate to="/qc" replace />} />
        <Route
          path="/qc/accept/:token"
          element={
            <RequireAuth requireRoles={QC_ACCEPT_ROLES} sect="qc">
              <AcceptScreenPage />
            </RequireAuth>
          }
        />
        <Route path="/technolog" element={<Navigate to="/technolog/dashboard" replace />} />
        <Route
          path="/technolog/dashboard"
          element={
            <RequireAuth requireRoles={TECHNOLOG_ROLES} sect="technolog">
              <TechnologDashboardPage />
            </RequireAuth>
          }
        />
        <Route
          path="/technolog/tech-docs"
          element={
            <RequireAuth requireRoles={TECHNOLOG_ROLES} sect="tech-docs">
              <TechDocsEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/technolog/accept"
          element={
            <RequireAuth requireRoles={["master", "admin"]} sect="technolog">
              <TechnologAcceptListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/technolog/accept/:token"
          element={
            <RequireAuth requireRoles={["master", "admin"]} sect="technolog">
              <TechnologAcceptDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/technolog/plan"
          element={
            <RequireAuth requireRoles={["master", "admin"]} sect="technolog">
              <TechnologPlanning />
            </RequireAuth>
          }
        />
        {/* «Крій · нагляд» прибрано 27.09.2026: дублював картку замовлення
            (цикли, чернетки, проблема крою — усе там; правка циклів — «Облік»
            у картці). Старі закладки ведуть у замовлення. */}
        <Route path="/cutting/report" element={<Navigate to="/orders" replace />} />
        <Route path="/cutting/report/:id" element={<ProjectToOrderRedirect />} />
        <Route path="/cutting/journal" element={<Navigate to="/orders" replace />} />
        {/* Планування злито в «Аналітика та план» (/technolog/dashboard),
            де Гант — окрема вкладка. Редірект для старих закладок. */}
        <Route path="/manager/plan" element={<Navigate to="/technolog/dashboard" replace />} />
        {/* Дошка більше не окрема сторінка — це режим перегляду всередині
            «Замовлень». Редірект для старих закладок і лендінгу менеджера. */}
        {/* Дошку замовлень прибрано 2026-08-30 — старе посилання веде на список. */}
        <Route path="/manager/kanban" element={<Navigate to="/orders" replace />} />
        <Route
          path="/owner/dashboard"
          element={
            <RequireAuth requireRoles={["admin"]} sect="dashboard">
              <OwnerDashboardPage />
            </RequireAuth>
          }
        />
        {/* Проєкти об'єднано із Замовленнями (кожен Order = Project) —
            редіректимо на єдине вікно /orders, щоб не дублювати. */}
        <Route path="/projects" element={<Navigate to="/orders" replace />} />
        <Route path="/projects/:id" element={<ProjectToOrderRedirect />} />
        {/* Кабінет закрійника — чистий URL /cutting (+ /cutting/:id деталь).
            Статичні /cutting/report і /cutting/journal рангуються вище за
            :id у React Router, тож конфлікту немає. */}
        <Route
          path="/cutting"
          element={
            <RequireAuth requireRoles={CUTTING_ROLES} sect="cutting">
              <CuttingWorkspaceListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cutting/:id"
          element={
            <RequireAuth requireRoles={CUTTING_ROLES} sect="cutting">
              <CuttingWorkspaceDetailPage />
            </RequireAuth>
          }
        />
        {/* «Хід замовлень» — лише перегляд; цеху видають у «Правах». `sect`
            обовʼязковий: без нього видане право не відкривало сторінку
            (01.10.2026 — ВТО бачила пункт меню, а сторінка — «не для вашої ролі»). */}
        <Route
          path="/progress"
          element={
            <RequireAuth requireRoles={PROGRESS_ROLES} sect="progress">
              <ProgressListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/progress/:id"
          element={
            <RequireAuth requireRoles={PROGRESS_ROLES} sect="progress">
              <ProgressDetailPage />
            </RequireAuth>
          }
        />
        {/* Старі /cutting/workspace* → редірект на чисті URL (сумісність). */}
        <Route path="/cutting/workspace" element={<Navigate to="/cutting" replace />} />
        <Route path="/cutting/workspace/:id" element={<CuttingWorkspaceLegacyRedirect />} />

        {/* Кабінет пакувальника — чистий URL /packing (+ /packing/:id деталь). */}
        <Route
          path="/packing"
          element={
            <RequireAuth requireRoles={PACKING_ROLES} sect="packing">
              <PackingWorkspaceListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/packing/:id"
          element={
            <RequireAuth requireRoles={PACKING_ROLES} sect="packing">
              <PackingWorkspaceDetailPage />
            </RequireAuth>
          }
        />
        <Route path="/packing/workspace" element={<Navigate to="/packing" replace />} />
        <Route path="/packing/workspace/:id" element={<PackingWorkspaceLegacyRedirect />} />

        {/* Відвантаження — фінал шляху замовлення */}
        <Route
          path="/shipping"
          element={
            <RequireAuth requireRoles={["vidgruzka", "menedzher", "master", "admin"]} sect="shipping">
              <ShippingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/problems"
          element={
            <RequireAuth requireRoles={["otk", "master", "admin", "menedzher"]} sect="alerts">
              <ProblemsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/print"
          element={
            // konstruktor — друк закрійної карти (власник 05.10.2026, Петельчук):
            // створювати/редагувати замовлення їй усе одно не можна (ORDER_WRITE_ROLES).
            <RequireAuth requireRoles={["admin", "master", "menedzher", "konstruktor"]}>
              <PrintOrders />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/labels"
          element={
            <RequireAuth requireRoles={["admin", "master", "menedzher", "konstruktor"]}>
              <PrintLabels />
            </RequireAuth>
          }
        />
        <Route
          path="/constructor"
          element={
            <RequireAuth requireRoles={["konstruktor", "master", "admin", "menedzher", "glav_tekhnolog"]} sect="constructor">
              <ConstructorBoard />
            </RequireAuth>
          }
        />
        <Route
          path="/stock/wh/:warehouse"
          element={
            <RequireAuth requireRoles={["admin", "menedzher", "buhgalter", "sklad_tkanyny", "sklad_furnitury", "zakriynyk"]} sect="stock">
              <WarehousePage />
            </RequireAuth>
          }
        />
        <Route
          path="/constructor/operations"
          element={
            <RequireAuth requireRoles={["glav_tekhnolog", "master", "admin", "konstruktor"]} sect="constructor">
              <OperationsCatalogPage />
            </RequireAuth>
          }
        />
        <Route
          path="/stock"
          element={
            <RequireAuth requireRoles={["menedzher", "master", "admin", "buhgalter"]} sect="stock">
              <StockPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/people"
          element={
            <RequireAuth requireRoles={["admin", "master", "menedzher"]} sect="seamstresses">
              <PeopleHub />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/access"
          element={
            <RequireAuth requireRoles={["admin"]}>
              <AccessMatrixPage />
            </RequireAuth>
          }
        />
        <Route
          path="/orders/:id/summary"
          element={
            <RequireAuth requireRoles={["menedzher", "master", "admin"]}>
              <ProjectSummaryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cash"
          element={
            <RequireAuth requireRoles={["admin", "buhgalter", "menedzher"]} sect="cash">
              <CashPage />
            </RequireAuth>
          }
        />
        <Route
          path="/technolog/norms"
          element={
            <RequireAuth requireRoles={TECHNOLOG_ROLES} sect="norms">
              <NormsPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </ConfirmProvider>
  );
}
